import Tabs from "@/components/Tabs";
import { IndexTabBarActionsProvider, useIndexTabBarActionIcon } from "@/components/Tabs/IndexTabBarActions";
import { Path, useNavigate } from "@/router";
import { Outlet, useLocation } from "react-router-dom";
import { useHasRoles } from "@/hooks/useCapabilities";
import useCapabilities from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import React from "react";

const tabs = ["my-jobs", "all-jobs", "execusearch"] as const;
type TabType = (typeof tabs)[number];

const TAB_ORDER: Record<TabType, number> = {
  "my-jobs": 0,
  "all-jobs": 1,
  execusearch: 2
};

export default function JobsTabs() {
  const location = useLocation();
  const { pathname, search } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();
  const { loaded } = useCapabilities();
  const hasAccessToMyJobs = useHasRoles([
    ROLE.GLOBAL_ADMIN,
    ROLE.CAREER_SERVICES_STAFF,
    ROLE.RECRUITING_STAFF,
    ROLE.TECHNOLOGY_STAFF
  ]);

  const tabOptions: Record<TabType, { label: string; path: string }> = {
    "all-jobs": {
      label: "Jobs",
      path: `/jobs/all-jobs`
    },
    execusearch: {
      label: "ExecuSearch",
      path: `/jobs/execusearch`
    },
    "my-jobs": {
      label: "My Jobs",
      path: `/jobs/my-jobs`
    }
  };

  const filteredTabItems = Object.entries(tabOptions)
    .filter(([key]) => key === "all-jobs" || key === "execusearch" || (key === "my-jobs" && hasAccessToMyJobs))
    .sort(([keyA], [keyB]) => TAB_ORDER[keyA as TabType] - TAB_ORDER[keyB as TabType])
    .map(([_, tab]) => ({
      label: tab.label,
      active: currentUrl.startsWith(tab.path),
      onClick: () => navigate(`${tab.path}${search}` as Path)
    }));

  React.useEffect(() => {
    if (loaded && currentUrl === "/jobs") {
      const defaultPath = hasAccessToMyJobs ? tabOptions["my-jobs"].path : tabOptions["all-jobs"].path;
      navigate(`${defaultPath}${search}` as Path, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUrl, hasAccessToMyJobs, navigate, loaded]);

  return (
    <IndexTabBarActionsProvider>
      <JobsTabBar tabItems={filteredTabItems} />
      <Outlet />
    </IndexTabBarActionsProvider>
  );
}

function JobsTabBar({ tabItems }: { tabItems: { label: string; active: boolean; onClick: () => void }[] }) {
  const actionIcon = useIndexTabBarActionIcon();
  return <Tabs tabItems={tabItems} actionIcon={actionIcon} />;
}
